import React, {useEffect, useRef} from 'react';
import {useUpdateNodeInternals} from 'reactflow';

import {FlowchartNode, isStaticTaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';

/**
 * After nodes are rendered with new handle positions, tell ReactFlow to update
 * its internal cache via `updateNodeInternals`. This triggers ReactFlow to
 * re-measure handle positions from the DOM.
 *
 * Handles needing refresh come from two sources:
 * 1. `nodeIdsToRefreshRef`: populated by REPLACE node changes (e.g., field reordering).
 * 2. Section clientId changes: detected here by comparing current vs previous render.
 *    This covers the save-complete path where `mergeIdsIntoPowProcedureFlowchartConfig`
 *    updates section clientIds with persisted parameter IDs, changing handle element
 *    IDs without going through `applyFlowchartChanges`.
 */
export function useNodeInternalsRefresh(
  interactiveNodes: Array<FlowchartNode>,
  nodeIdsToRefreshRef: React.MutableRefObject<Set<string>>
): void {
  const prevSectionClientIdsRef = useRef<Record<string, string>>({});
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    const nodeIdsToRefresh = new Set(nodeIdsToRefreshRef.current);
    nodeIdsToRefreshRef.current.clear();

    // Section row clientIds are used as part of ReactFlow Handle element IDs
    // (via makeDataFlowEdgeHandle), so a change means new handle DOM elements
    // that ReactFlow doesn't know about yet.
    for (const node of interactiveNodes) {
      if (isStaticTaskSchemaNodeData(node.data)) {
        const handleKey = node.data.sections.flatMap((s) => s.sectionRows.map((r) => r.clientId)).join('|');
        const prevKey = prevSectionClientIdsRef.current[node.id];
        // Skip initial render (prevKey == null) — only refresh on actual changes.
        if (prevKey != null && prevKey !== handleKey) {
          nodeIdsToRefresh.add(node.id);
        }
        prevSectionClientIdsRef.current[node.id] = handleKey;
      }
    }

    if (nodeIdsToRefresh.size > 0) {
      // Double requestAnimationFrame ensures we wait for:
      // 1. First frame: React commits DOM changes for interactiveNodes update.
      // 2. Second frame: Node components render with new handle positions.
      // Only then can updateNodeInternals measure the correct handle positions.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nodeIdsToRefresh.forEach((nodeId) => {
            updateNodeInternals(nodeId);
          });
        });
      });
    }
  }, [interactiveNodes, updateNodeInternals, nodeIdsToRefreshRef]);
}
